/*
 * Drives the Trusted Shops review figures on a product page: the star rating in
 * the sticky buy bar, and whether the "Bewertungen" accordion appears at all.
 *
 * Why not Liquid
 * --------------
 * The bar used to read `product.metafields.reviews.rating` — the shared
 * namespace review apps write into. Judge.me wrote there; Trusted Shops does
 * not. After the switch those metafields froze, and products advertised ratings
 * that no longer had reviews behind them (one showed "5.0 (5 Bewertungen)" over
 * a widget listing a single review). Under German UWG that is not merely untidy.
 *
 * Trusted Shops publishes per-product ratings only to the browser, so there is
 * nothing for the server-rendered page to read. Hence this.
 *
 * Where the numbers come from
 * ---------------------------
 * The eTrusted widget renders into a CLOSED shadow root, so its DOM cannot be
 * read. It does, however, feed itself from public JSON endpoints declared in its
 * own manifest, and the grades feed is exactly the aggregate this needs:
 *
 *   /feeds/grades/v1/channels/{channel}/products/sku/{sku}/feed.json
 *   -> { grades: { overall: { rating, count, distribution }, 90days: {…}, … } }
 *
 * The SKU segment is the identifier hex-encoded, one byte per character
 * ("53112735564115" -> "3533313132373335353634313135"). That is their path
 * encoding, not obfuscation, and it is what the widget itself requests.
 *
 * One request PER VARIANT. Trusted Shops keys reviews to the variant, and the
 * feed rejects a comma-separated list (it answers 200 with `{}`), so the widget
 * asks about each in turn and adds the results up — which is why a four-colour
 * towel with both its reviews on "Grau" showed "2 Bewertungen" in the widget and
 * nothing in the bar back when this asked about the selected variant alone.
 *
 * `overall` is used rather than the rolling windows, to match the figure the
 * widget prints under the product.
 */
(function () {
  'use strict';

  /* Per-shop, stable, and not exposed anywhere the page can read it — the
     widget carries it inside its closed shadow root, and the Trusted Shops app
     config endpoint does not return it. To find it again: open a product page,
     DevTools > Network, filter "feeds", and read the channel id out of any
     integrations.etrusted.com URL. */
  var CHANNEL = 'chl-c3389305-6a47-400e-bbdf-8c98ad8751ab';
  var FEED = 'https://integrations.etrusted.com/feeds/grades/v1/channels/{channel}/products/sku/{sku}/feed.json';

  /* The review list widget the app is configured to show. Same story as the
     channel id: per-shop, stable, and only discoverable by reading it off the
     element the app injects (or from its Network requests). */
  var WIDGET_ID = 'wdg-c8de202e-d5d7-4a67-81aa-6bcccbf4955a';
  var WIDGET_HOST = '#etrusted-product_review_list';

  /* The bootstrap that defines the <etrusted-widget> custom element and renders
     it. Taken verbatim from the `bootstrapTag` of the app's own config, so this
     loads exactly what the app loads where it is configured — no attributes on
     it, because it reads everything off the widget elements it finds. */
  var WIDGET_RUNTIME = 'https://integrations.etrusted.com/applications/widget.js/v2';

  /* How long to let the app insert its own widget before doing it for it. Long
     enough that a working market never sees ours (measured: the app inserts
     within ~500ms there), short enough not to hold the reviews back. */
  var WIDGET_GRACE_MS = 1500;

  /* One request per variant, so a product with a long option matrix could fan
     out badly. Nothing here approaches this, and the cap only ever costs a few
     reviews off the count rather than breaking the page. */
  var MAX_VARIANTS = 25;

  var COUNT_SENTINEL = '999999';

  function hexEncode(value) {
    var out = '';
    var text = String(value);
    for (var i = 0; i < text.length; i++) {
      out += text.charCodeAt(i).toString(16).padStart(2, '0');
    }
    return out;
  }

  function skusFrom(el) {
    if (!el || !el.dataset.tsSkus) return [];
    return el.dataset.tsSkus
      .split(',')
      .map(function (s) { return s.trim(); })
      .filter(Boolean)
      .slice(0, MAX_VARIANTS);
  }

  /* Resolves to {ok, grade} and never rejects.

     `ok` says whether the answer can be trusted, and it is kept separate from
     `grade` on purpose: "this variant has no reviews" and "this request did not
     come back" must not collapse into the same value. They did once — a non-OK
     response was turned into null, read as "no reviews", and the whole
     Bewertungen tab was pulled off a product that has some. That surfaced as the
     tab appearing in one browser and not another: the variant requests go out in
     parallel and cold in a fresh profile, so one failing is a matter of timing
     rather than of anything on the page. */
  function fetchGrade(sku) {
    var url = FEED.replace('{channel}', CHANNEL).replace('{sku}', hexEncode(sku));
    return fetch(url, { credentials: 'omit' })
      .then(function (response) {
        if (!response.ok) return { ok: false };

        return response.json().then(function (data) {
          /* A variant with no reviews answers 200 with `{}` rather than 404, so
             the absence of `overall` is the real "no reviews" signal. */
          var overall = data && data.grades && data.grades.overall;
          if (!overall || !overall.count) return { ok: true, grade: null };
          return {
            ok: true,
            grade: { rating: Number(overall.rating), count: Number(overall.count) },
          };
        });
      })
      .catch(function () {
        return { ok: false };
      });
  }

  /* Weighted by count, not a plain mean of the per-variant averages: three
     five-star reviews on one variant and one one-star on another is 4.0, and
     averaging 5 and 1 would call it 3.0.

     `complete` is false when any variant went unanswered — the totals are then
     a floor rather than a figure, so the caller neither prints them nor acts on
     a zero. */
  function combine(results) {
    var count = 0;
    var weighted = 0;
    var complete = true;

    for (var i = 0; i < results.length; i++) {
      if (!results[i].ok) {
        complete = false;
        continue;
      }
      if (!results[i].grade) continue;
      count += results[i].grade.count;
      weighted += results[i].grade.rating * results[i].grade.count;
    }

    return {
      complete: complete,
      total: count ? { rating: weighted / count, count: count } : null,
    };
  }

  function formatCount(el, count) {
    var template = count === 1 ? el.dataset.countOne : el.dataset.countOther;
    if (!template) return '';
    return template.replace(COUNT_SENTINEL, String(count));
  }

  /* One decimal, matching the widget ("5,00" there, "5.0" here — the bar has
     always used a dot, and changing that is a separate decision). */
  function formatRating(rating) {
    return (Math.round(rating * 10) / 10).toFixed(1);
  }

  function paintRating(el, rating, count) {
    var stars = el.querySelectorAll('.sticky--star');
    var filled = Math.round(rating);
    for (var i = 0; i < stars.length; i++) {
      stars[i].classList.toggle('is-filled', i < filled);
    }

    var label = formatRating(rating);
    var text = label;
    if (count > 0) text += ' (' + formatCount(el, count) + ')';

    var stripe = el.querySelector('.sticky--stars');
    if (stripe) stripe.setAttribute('aria-label', label);

    var textEl = el.querySelector('.sticky--rating-text');
    if (textEl) textEl.textContent = text;

    el.hidden = false;

    /* Lets the stylesheet keep the meta segment and its divider collapsed on
       products that turn out to have no reviews. */
    var wrapper = el.closest('.sticky--add-to-cart-wrapper');
    if (wrapper) wrapper.classList.add('has-ts-rating');
  }

  /* Puts the review list widget on the page when the app fails to.
   *
   * The app block renders only an empty container server-side —
   *
   *   <div id="etrusted-product_review_list">
   *     <!-- BEGIN app snippet: insert-widget-sku -->
   *     <!-- END app snippet -->
   *   </div>
   *
   * — and its own script (esc.js) is what fills it in from the browser, off a
   * config it fetches per market:
   *
   *   /api/esc-config/{shop}/de/RO -> full config, widget included
   *   /api/esc-config/{shop}/de/DE -> {"trustBadgeHtml":"…","widgetsHTML":{}}
   *
   * Only German+Romania is mapped to the Trusted Shops channel, and a fresh
   * session on this shop defaults to German+Germany — so every real customer got
   * the empty one and no reviews at all. It looked like flakiness because a
   * browser that had once been on the Romanian market kept that in a cookie and
   * carried on working.
   *
   * Off that config esc.js injects TWO things: the widget element, and the
   * bootstrap that defines <etrusted-widget>. With an empty config it injects
   * neither, and an element created by hand stays an inert unknown tag — so both
   * halves have to be replaced, which is why ensureRuntime exists alongside this.
   *
   * The presence checks keep both a fallback: whatever the app manages on its
   * own is left alone, and the page ends up with exactly one widget and one
   * runtime.
   *
   * The clean fix is Channel mapping in the Trusted Shops app, which was out of
   * reach here. If that is ever corrected, this and ensureRuntime can go, along
   * with WIDGET_ID and WIDGET_RUNTIME.
   */
  function ensureRuntime() {
    if (window.customElements && customElements.get('etrusted-widget')) return;
    if (document.querySelector('script[src="' + WIDGET_RUNTIME + '"]')) return;

    var script = document.createElement('script');
    script.src = WIDGET_RUNTIME;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }

  function ensureWidget(skus) {
    var host = document.querySelector(WIDGET_HOST);
    if (!host) return;

    var widget = host.querySelector('etrusted-widget');

    if (!widget) {
      widget = document.createElement('etrusted-widget');
      widget.setAttribute('data-etrusted-widget-id', WIDGET_ID);
      widget.setAttribute('data-sku', skus.join(','));
      widget.setAttribute('data-ts-fallback', '');
      host.appendChild(widget);
    }

    /* Checked even when the app supplied the element: the two halves come from
       the same config, but one arriving is no guarantee of the other. */
    ensureRuntime();

    /* If the app turns up late with its own element, drop ours rather than
       leave the visitor with the review list printed twice. */
    if (widget.hasAttribute('data-ts-fallback')) {
      var observer = new MutationObserver(function () {
        if (!host.querySelector('etrusted-widget:not([data-ts-fallback])')) return;
        widget.remove();
        observer.disconnect();
      });
      observer.observe(host, { childList: true });
      setTimeout(function () {
        observer.disconnect();
      }, 15000);
    }
  }

  function init() {
    var row = document.querySelector('[data-ts-rating]');
    var accordion = document.querySelector('[data-ts-reviews-accordion]');
    if (!row && !accordion) return;

    /* Either element carries the same list; whichever is on the page wins. */
    var skus = skusFrom(row);
    if (!skus.length) skus = skusFrom(accordion);
    if (!skus.length) return;

    setTimeout(function () {
      ensureWidget(skus);
    }, WIDGET_GRACE_MS);

    Promise.all(skus.map(fetchGrade))
      .then(function (results) {
        var answer = combine(results);

        /* Anything unanswered and the page is left exactly as rendered: tab in
           place, stars hidden. The widget makes its own request and may well
           succeed where ours failed, so pulling the tab on incomplete evidence
           would hide reviews that are about to appear — and printing a total
           that is missing a variant would understate the product. */
        if (!answer.complete) return;

        /* Confirmed empty: take the tab away. It ships visible so the widget
           inside it can initialise — it will not do that inside a display:none
           container — so this is a retraction, not a reveal. See the comment on
           the markup in product-details.liquid. */
        if (!answer.total) {
          if (accordion) accordion.hidden = true;
          return;
        }

        if (row) paintRating(row, answer.total.rating, answer.total.count);
      })
      .catch(function () {
        /* fetchGrade already absorbs its own failures, so this only catches a
           bug in the code above. Same rule: leave the page as rendered. */
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
